import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

import Breadcrumb from '@/components/breadcrumb';

import { useKitPairsSection } from './use-kit-pairs-section';

const formatDate = (value: string) => format(new Date(value), 'dd/MM/yyyy HH:mm');

export default function KitPairsSection() {
  const hooks = useKitPairsSection();

  const selectedProduct = hooks.kitProducts.find((item) => item.id === hooks.selectedProductId);

  const columns: GridColDef[] = [
    { field: 'principalSerial', headerName: 'Serie caja', flex: 1, minWidth: 180 },
    { field: 'complementSerial', headerName: 'Serie control', flex: 1, minWidth: 180 },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 170,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value as string),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          color="error"
          onClick={() => hooks.onDelete(params.row.id as string)}
          disabled={hooks.isLoadingList}
          title="Eliminar pareja"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <div className="p-6 flex flex-col gap-4 pb-12">
      <Breadcrumb
        sections={[{ label: 'Izzi' }, { label: 'Vincular kit' }]}
        description="Registra la relación entre la serie de caja (35223) y la serie de control (35220)"
      />

      <div className="w-full max-w-5xl mx-auto flex flex-col gap-6">
        <Card variant="outlined">
          <CardContent className="flex flex-col gap-6">
            <Typography variant="subtitle1" fontWeight="bold">
              Registrar pareja caja + control
            </Typography>

            <TextField
              select
              label="Producto kit"
              variant="outlined"
              fullWidth
              value={hooks.selectedProductId}
              onChange={hooks.onChangeSelectedProduct}
              disabled
              helperText="El backend asigna los SKU: 35223 (caja) y 35220 (control)"
            >
              {hooks.kitProducts.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.label} ({product.principalSku} + {product.complementSku})
                </MenuItem>
              ))}
            </TextField>

            {selectedProduct && (
              <Typography variant="body2" color="text.secondary">
                Principal: SKU {selectedProduct.principalSku} (caja) · Complemento: SKU{' '}
                {selectedProduct.complementSku} (control)
              </Typography>
            )}

            <TextField
              label="Serie caja (35223)"
              variant="outlined"
              fullWidth
              required
              value={hooks.principalSerial}
              onChange={hooks.onChangePrincipalSerial}
              onKeyDown={hooks.onKeyDownPrincipalSerial}
              placeholder="Escanee la serie de la caja"
              disabled={hooks.isSaving}
              autoFocus
              inputRef={hooks.principalInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScannerIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <div className="flex gap-4 items-start">
              <TextField
                label="Serie control (35220)"
                variant="outlined"
                fullWidth
                required
                value={hooks.complementSerial}
                onChange={hooks.onChangeComplementSerial}
                onKeyDown={hooks.onKeyDownComplementSerial}
                placeholder="Escanee la serie del control"
                disabled={hooks.isSaving}
                inputRef={hooks.complementInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeScannerIcon color={hooks.canSave ? 'primary' : 'disabled'} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={hooks.onSave}
                disabled={!hooks.canSave}
                sx={{ minWidth: 130, height: 56 }}
              >
                {hooks.isSaving ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent className="flex flex-col gap-4">
            <Typography variant="subtitle1" fontWeight="bold">
              Parejas registradas
            </Typography>

            <div className="flex gap-3 items-center">
              <TextField
                label="Filtrar por serie caja"
                variant="outlined"
                size="small"
                value={hooks.filterPrincipalSerial}
                onChange={hooks.onChangeFilterPrincipalSerial}
                onKeyDown={hooks.onKeyDownFilter}
                sx={{ minWidth: 280 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={hooks.onSearch} disabled={hooks.isLoadingList}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {hooks.appliedFilter && (
                <Button variant="outlined" color="inherit" onClick={hooks.onClearFilter}>
                  Limpiar filtro
                </Button>
              )}
            </div>

            <div className="bg-white rounded-2xl" style={{ height: 480, width: '100%' }}>
              <DataGrid
                rows={hooks.pairs}
                columns={columns}
                loading={hooks.isLoadingList}
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={hooks.totalRows}
                paginationModel={hooks.paginationModel}
                onPaginationModelChange={hooks.onChangePaginationModel}
                pageSizeOptions={[10, 20, 50]}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
                  '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #e5e7eb' },
                  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                }}
                localeText={{ noRowsLabel: 'No hay parejas registradas.' }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
